import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ClockIcon, CheckCircleIcon, EditIcon, TrashIcon, BellIcon, CalendarIcon, PillIcon, ChevronDownIcon, ChevronUpIcon, HistoryIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { AddReminderModal } from '../components/modals/AddReminderModal';
import { MedicineReminderCard } from '../components/shared/MedicineReminderCard';
interface Reminder {
  id: string;
  medicineName: string;
  medicineStrength: string;
  medicineForm: string;
  dosage: string;
  frequency: string;
  timesPerDay: number;
  doseTimes: string[];
  daysOfWeek: number[];
  mealTiming: 'before' | 'after' | 'with';
  pillsPerDose: number;
  dosesPerIntake: number;
  currentStock: number;
  autoReorder: boolean;
  nextDose: Date;
  duration: number;
  reminderType: string[];
  notes?: string;
  status: 'active' | 'completed';
  createdAt: Date;
  snoozedUntil?: Date;
}
export function MedicineReminderPage() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  // Load reminders from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('medicineReminders');
    if (stored) {
      const parsed = JSON.parse(stored);
      setReminders(parsed.map((r: any) => ({
        ...r,
        nextDose: new Date(r.nextDose),
        createdAt: new Date(r.createdAt)
      })));
    } else {
      // Sample data
      setReminders([{
        id: '1',
        medicineName: 'Panadol Extra',
        medicineStrength: '500mg',
        medicineForm: 'Tablet',
        dosage: '1 tablet',
        frequency: 'Every 8 hours',
        timesPerDay: 3,
        doseTimes: ['08:00', '16:00', '00:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'after',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 20,
        autoReorder: true,
        nextDose: new Date(Date.now() + 2 * 60 * 60 * 1000),
        duration: 7,
        reminderType: ['push', 'sms'],
        notes: 'Take after meals',
        status: 'active',
        createdAt: new Date()
      }, {
        id: '2',
        medicineName: 'Vitamin D3',
        medicineStrength: '1000 IU',
        medicineForm: 'Capsule',
        dosage: '1 capsule',
        frequency: 'Once daily',
        timesPerDay: 1,
        doseTimes: ['09:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'with',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 30,
        autoReorder: false,
        nextDose: new Date(Date.now() + 5 * 60 * 60 * 1000),
        duration: 30,
        reminderType: ['push'],
        status: 'active',
        createdAt: new Date()
      }]);
    }
  }, []);
  // Save reminders to localStorage
  useEffect(() => {
    if (reminders.length > 0) {
      localStorage.setItem('medicineReminders', JSON.stringify(reminders));
    }
  }, [reminders]);
  const activeReminders = reminders.filter(r => r.status === 'active');
  const completedReminders = reminders.filter(r => r.status === 'completed');
  const handleAddReminder = (reminder: Omit<Reminder, 'id' | 'createdAt'>) => {
    const newReminder: Reminder = {
      ...reminder,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setReminders([...reminders, newReminder]);
    setShowAddModal(false);
  };
  const handleEditReminder = (id: string, updated: Omit<Reminder, 'id' | 'createdAt'>) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...updated,
      id: r.id,
      createdAt: r.createdAt
    } : r));
    setEditingReminder(null);
    setShowAddModal(false);
  };
  const handleMarkTaken = (id: string) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      status: 'completed' as const
    } : r));
  };
  const handleDeleteReminder = (id: string) => {
    setReminders(reminders.filter(r => r.id !== id));
  };
  const handleSnooze = (id: string) => {
    const snoozeUntil = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes from now
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      snoozedUntil: snoozeUntil,
      nextDose: snoozeUntil
    } : r));
  };
  const getNextDoseText = (nextDose: Date) => {
    const now = new Date();
    const diff = nextDose.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    if (diff < 0) return 'Overdue';
    if (hours === 0) return `In ${minutes} min`;
    if (hours < 24) return `In ${hours}h ${minutes}m`;
    return `In ${Math.floor(hours / 24)} days`;
  };
  const sortedActiveReminders = [...activeReminders].sort((a, b) => a.nextDose.getTime() - b.nextDose.getTime());
  const nextReminder = sortedActiveReminders[0];
  return <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7273" data-mp="1m1c2dbx6u6iy">
      {/* Desktop Header */}
      <div className="hidden md:block" data-id="element-7274" data-mp="1m3ptj9oqyyl8">
        <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7275" data-mp="1u2t2jnymenk0" />
      </div>
      {/* Mobile Internal Header */}
      <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-7276" data-mp="281kfh71qy36k">
        <MobileInternalHeader title="Medicine Reminders" data-id="element-7277" data-mp="28118gldmwz7b" />
      </div>
      <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-7278" data-mp="1af3ik2wxdrlc">
        {/* Page Hero Breadcrumb */}
        <PageHeroBreadcrumb breadcrumbs={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Medicine Reminders'
      }]} title="Medicine Reminders" description="Never miss a dose. Set up personalized reminders for your medications and track your medication schedule." data-id="element-7279" data-mp="1fdol48zngncy" />
        <div className="container mx-auto px-4 py-8" data-id="element-7280" data-mp="wl2ty1zotkyp">
          {/* Next Dose Alert */}
          {nextReminder && <div className="mb-6 bg-gradient-to-r from-[#1e2b55] to-[#162041] rounded-xl p-4 text-white shadow-lg" data-id="element-7281" data-mp="p11zhrv7n6xm">
              <div className="flex items-start justify-between" data-id="element-7282" data-mp="k273fo88flvk">
                <div className="flex-1" data-id="element-7283" data-mp="f3dso3kby8z3">
                  <div className="flex items-center gap-2 mb-2" data-id="element-7284" data-mp="a5vpyuwwopzj">
                    <BellIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7285" data-mp="a5m3hhb2l0yl" />
                    <span className="text-sm font-semibold" data-id="element-7286" data-mp="65burwvw7wg1">Next Dose</span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-7287" data-mp="1ciq190fco3y">
                    {nextReminder.medicineName}
                  </h3>
                  <p className="text-sm text-blue-100 mb-3" data-id="element-7288" data-mp="3sk4nul0gpwcw">
                    {nextReminder.dosage} •{' '}
                    {getNextDoseText(nextReminder.nextDose)}
                  </p>
                  <div className="flex gap-2" data-id="element-7289" data-mp="3il5ndppup70z">
                    <button onClick={() => handleMarkTaken(nextReminder.id)} className="bg-[#119c4b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f8a42] transition-colors" data-id="element-7290" data-mp="3ekc4jar9miey">
                      ✅ Mark as Taken
                    </button>
                    <button onClick={() => handleSnooze(nextReminder.id)} className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-30 transition-colors" data-id="element-7291" data-mp="wc6cf65rrd12">
                      ⏰ Snooze 10 min
                    </button>
                  </div>
                </div>
                <ClockIcon className="w-12 h-12 text-blue-200 opacity-50" data-id="element-7292" data-mp="dmyk3xf9i1pi" />
              </div>
            </div>}
          {/* Active Reminders */}
          <div className="mb-8" data-id="element-7293" data-mp="im2yr3kygrn8">
            <div className="flex items-center justify-between mb-4" data-id="element-7294" data-mp="4ma97bl60hgu">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-7295" data-mp="4o4tqosiy7ke">
                Active Reminders ({activeReminders.length})
              </h2>
              <button onClick={() => setShowAddModal(true)} className="hidden md:flex items-center gap-2 bg-[#1e2b55] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7296" data-mp="3m47fx3b9wrdo">
                <PlusIcon className="w-4 h-4" data-id="element-7297" data-mp="3v6p43nahnu3b" />
                Add Reminder
              </button>
            </div>
            {activeReminders.length === 0 ? <div className="bg-white rounded-xl p-8 text-center shadow-sm" data-id="element-7298" data-mp="8x5plrbp9six">
                <PillIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-7299" data-mp="3zmayqb3bdqg" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-7300" data-mp="hqhoifwjaj0d">
                  No Active Reminders
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-7301" data-mp="donrqipglz5h">
                  Start by adding your first medication reminder
                </p>
                <button onClick={() => setShowAddModal(true)} className="bg-[#1e2b55] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7302" data-mp="royillqrrdqn">
                  Add Your First Reminder
                </button>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7303" data-mp="18fkvrp4nlwjr">
                {sortedActiveReminders.map(reminder => <MedicineReminderCard key={reminder.id} reminder={reminder} onMarkTaken={handleMarkTaken} onEdit={r => {
              setEditingReminder(r);
              setShowAddModal(true);
            }} onDelete={handleDeleteReminder} onSnooze={handleSnooze} data-id="element-7304" data-mp="zhsbe6k2rypi" />)}
              </div>}
          </div>
          {/* Smart Suggestions */}
          {completedReminders.length > 0 && <div className="mb-8" data-id="element-7305" data-mp="6rilyznat6oc">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-7306" data-mp="6qh2q7w4pdvj">
                💡 Smart Suggestions
              </h2>
              <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-7307" data-mp="3sroeo6hnioqx">
                <p className="text-sm text-gray-600 mb-3" data-id="element-7308" data-mp="1nznwhqx2hli">
                  Based on your history
                </p>
                <div className="space-y-2" data-id="element-7309" data-mp="6kzuctrohmd5">
                  {completedReminders.slice(0, 2).map(reminder => <button key={reminder.id} onClick={() => {
                setEditingReminder({
                  ...reminder,
                  id: Date.now().toString(),
                  status: 'active',
                  nextDose: new Date()
                });
                setShowAddModal(true);
              }} className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-7310" data-mp="beuge3uradfe">
                      <div className="flex items-center gap-3" data-id="element-7311" data-mp="2w53b7s1judjh">
                        <PillIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7312" data-mp="2o3c52iyofer3" />
                        <div className="text-left" data-id="element-7313" data-mp="2j3zlq870kpt9">
                          <p className="text-sm font-medium text-gray-900" data-id="element-7314" data-mp="2j5kebs9rpyoh">
                            {reminder.medicineName}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-7315" data-mp="2t0m14hgkmonw">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                        </div>
                      </div>
                      <PlusIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7316" data-mp="321sl50ifihlw" />
                    </button>)}
                </div>
              </div>
            </div>}
          {/* History Section */}
          <div className="mb-8" data-id="element-7317" data-mp="3bu833xtq766s">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-7318" data-mp="3prmvnbf9zu2z">
              <div className="flex items-center gap-3" data-id="element-7319" data-mp="8aackb1eu84n">
                <HistoryIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7320" data-mp="d8brqkvptnau" />
                <h2 className="text-xl font-bold text-gray-900" data-id="element-7321" data-mp="d7iuqbmk6c5w">
                  📜 History ({completedReminders.length})
                </h2>
              </div>
              {showHistory ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-7322" data-mp="45ssear51stc" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-7323" data-mp="3lnc3236qkhbv" />}
            </button>
            {showHistory && <div className="mt-4 space-y-3" data-id="element-7324" data-mp="3qgopq7ruue1z">
                {completedReminders.length === 0 ? <div className="bg-white rounded-xl p-6 text-center" data-id="element-7325" data-mp="3clmc0ygrnkem">
                    <p className="text-gray-500" data-id="element-7326" data-mp="3gpaitexkqbyt">No completed reminders yet</p>
                  </div> : completedReminders.map(reminder => <div key={reminder.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-gray-300" data-id="element-7327" data-mp="37n1amvps2uol">
                      <div className="flex items-start justify-between" data-id="element-7328" data-mp="9vu4t5dnlk6h">
                        <div className="flex-1" data-id="element-7329" data-mp="53sx7wrfkhms">
                          <div className="flex items-center gap-2 mb-1" data-id="element-7330" data-mp="52htfwxzj9vo">
                            <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-7331" data-mp="3wsfvawsm66" />
                            <h3 className="font-semibold text-gray-600" data-id="element-7332" data-mp="3rbsil1jgsdnz">
                              {reminder.medicineName}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500" data-id="element-7333" data-mp="3iab1pgy8g7y8">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-7334" data-mp="3ddkp53lmzuhk">
                            Completed on{' '}
                            {reminder.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteReminder(reminder.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-7335" data-mp="1j45u4k5rizp9">
                          <TrashIcon className="w-4 h-4" data-id="element-7336" data-mp="1sus1jr3bsjwv" />
                        </button>
                      </div>
                    </div>)}
              </div>}
          </div>
        </div>
      </main>
      {/* Floating Action Button (Mobile) */}
      <button onClick={() => setShowAddModal(true)} className="md:hidden fixed bottom-20 right-4 w-14 h-14 bg-[#119c4b] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#0f8a42] transition-colors z-40" data-id="element-7337" data-mp="154xszlq1hhif">
        <PlusIcon className="w-6 h-6" data-id="element-7338" data-mp="2li4nbz1nwnp4" />
      </button>
      {/* Add/Edit Reminder Modal */}
      <AddReminderModal isOpen={showAddModal} onClose={() => {
      setShowAddModal(false);
      setEditingReminder(null);
    }} onSave={editingReminder ? reminder => handleEditReminder(editingReminder.id, reminder) : handleAddReminder} initialData={editingReminder} data-id="element-7339" data-mp="2lkh1e14audww" />
      <Footer data-id="element-7340" data-mp="zf4jei97xywf" />
      <MobileFooterNav data-id="element-7341" data-mp="zdjyzlgmbwca" />
    </div>;
}